import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

import {
  weekendPaceBellPepper,
  weekendPaceChili,
  weekendPaceChiliFire,
} from "src/static/img";

const WEEKEND_PACE_DESCRIPTIONS: Record<number, string> = {
  0: "Down to match whatever energy or pace a group wants to set, chill or intense!",
  1: "Chill pace. Enjoying activities, workshops, and keeping things low stress.",
  2: "Steady rhythm. Moving at a focused pace while leaving room for activities.",
  3: "Locked in. High momentum, no brakes, here to win.",
};

const WEEKEND_PACE_ICONS: Record<
  number,
  { count: number; src: string; width: number }
> = {
  0: { count: 1, src: weekendPaceBellPepper, width: 17.894 },
  1: { count: 1, src: weekendPaceChili, width: 23.835 },
  2: { count: 2, src: weekendPaceChili, width: 23.835 },
  3: { count: 3, src: weekendPaceChiliFire, width: 23.835 },
};

const WeekendPace: React.FC<{ profileId: number; value: number }> = ({
  profileId,
  value,
}) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ left: 0, top: 0 });
  const tooltipAnchorRef = useRef<HTMLSpanElement>(null);
  const pace = Math.max(0, Math.min(3, Math.round(value)));
  const paceIcons = WEEKEND_PACE_ICONS[pace];
  const tooltipId = `weekend-pace-tooltip-${profileId}`;

  useEffect(() => {
    if (!isTooltipOpen) return;

    const updateTooltipPosition = () => {
      const bounds = tooltipAnchorRef.current?.getBoundingClientRect();
      if (bounds) {
        setTooltipPosition({
          left: bounds.left + bounds.width / 2,
          top: bounds.top,
        });
      }
    };
    const closeOnOutsideInteraction = (event: MouseEvent | TouchEvent) => {
      if (!tooltipAnchorRef.current?.contains(event.target as Node)) {
        setIsTooltipOpen(false);
      }
    };

    updateTooltipPosition();
    document.addEventListener("mousedown", closeOnOutsideInteraction);
    document.addEventListener("scroll", updateTooltipPosition, true);
    document.addEventListener("touchstart", closeOnOutsideInteraction);
    window.addEventListener("resize", updateTooltipPosition);

    return () => {
      document.removeEventListener("mousedown", closeOnOutsideInteraction);
      document.removeEventListener("scroll", updateTooltipPosition, true);
      document.removeEventListener("touchstart", closeOnOutsideInteraction);
      window.removeEventListener("resize", updateTooltipPosition);
    };
  }, [isTooltipOpen]);

  const showTooltip = () => {
    const bounds = tooltipAnchorRef.current?.getBoundingClientRect();
    if (bounds) {
      setTooltipPosition({
        left: bounds.left + bounds.width / 2,
        top: bounds.top,
      });
    }
    setIsTooltipOpen(true);
  };

  return (
    <Pace>
      <PaceLabel>My Weekend Pace</PaceLabel>
      <PaceTooltipAnchor
        ref={tooltipAnchorRef}
        onMouseEnter={showTooltip}
        onMouseLeave={() => {
          if (!tooltipAnchorRef.current?.contains(document.activeElement)) {
            setIsTooltipOpen(false);
          }
        }}
      >
        <PaceIcons
          type="button"
          aria-label={`Weekend pace ${pace}. Show description`}
          aria-describedby={isTooltipOpen ? tooltipId : undefined}
          onClick={showTooltip}
          onFocus={showTooltip}
          onBlur={() => setIsTooltipOpen(false)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.stopPropagation();
              setIsTooltipOpen(false);
            }
          }}
        >
          {Array.from({ length: paceIcons.count }, (_, index) => (
            <PaceIcon
              key={index}
              src={paceIcons.src}
              $width={paceIcons.width}
              alt=""
              aria-hidden
            />
          ))}
        </PaceIcons>
        {isTooltipOpen &&
          createPortal(
            <PaceTooltip
              id={tooltipId}
              role="tooltip"
              $left={tooltipPosition.left}
              $top={tooltipPosition.top}
            >
              {WEEKEND_PACE_DESCRIPTIONS[pace]}
            </PaceTooltip>,
            document.body
          )}
      </PaceTooltipAnchor>
    </Pace>
  );
};

const Pace = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 16px;
`;

const PaceLabel = styled.span`
  color: ${({ theme }) => theme.globalConstants.color.textTertiary};
  font-size: 16px;
  font-weight: 700;
  line-height: normal;
`;

const PaceTooltipAnchor = styled.span`
  position: relative;
  display: inline-flex;
`;

const PaceIcons = styled.button`
  display: flex;
  min-width: 24px;
  min-height: 24px;
  padding: 0;
  align-items: center;
  justify-content: center;
  gap: 3px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  cursor: help;

  &:focus-visible {
    box-shadow: 0 0 0 2px
      ${({ theme }) => theme.globalConstants.color.backgroundPrimaryLight};
    outline: none;
  }
`;

const PaceIcon = styled.img<{ $width: number }>`
  width: ${({ $width }) => $width}px;
  height: 24px;
`;

const PaceTooltip = styled.span<{ $left: number; $top: number }>`
  position: fixed;
  z-index: 1001;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  box-sizing: border-box;
  width: 210px;
  max-width: calc(100vw - 48px);
  padding: 8px 12px;
  transform: translate(-50%, calc(-100% - 8px));
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
  background: ${({ theme }) => theme.globalConstants.color.white};
  box-shadow: 0 1px 2px rgb(0 0 0 / 6%), 0 1px 3px rgb(0 0 0 / 10%);
  color: ${({ theme }) => theme.globalConstants.color.textPrimary};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  pointer-events: none;
  text-align: center;

  &::before,
  &::after {
    position: absolute;
    left: 50%;
    width: 0;
    height: 0;
    transform: translateX(-50%);
    border-right: 7px solid transparent;
    border-left: 7px solid transparent;
    content: "";
  }

  &::before {
    bottom: -8px;
    border-top: 8px solid
      ${({ theme }) => theme.globalConstants.color.borderGray};
  }

  &::after {
    bottom: -7px;
    border-top: 8px solid ${({ theme }) => theme.globalConstants.color.white};
  }
`;

export default WeekendPace;
